import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useDeckGroups } from '@/queries/deck-groups';
import { DeckGroupCard } from './DeckGroupCard';
import { CircularLoader } from './CircularLoader';
import { debounce } from '@/lib/utils';
import BookCheckIcon from '@/assets/common/book-check.svg?react';
import styles from './DeckGroupList.module.scss';

export function DeckGroupList() {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: deckGroups, isLoading, error } = useDeckGroups({
    search: searchTerm || undefined,
  });

  const handleSearch = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  if (isLoading) {
    return (
      <div className={styles.deckGroupList__loading}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.deckGroupList}>
        <div className={styles.deckGroupList__error}>
          Error loading deck groups: {error.message}
        </div>
      </div>
    );
  }

  const isEmpty = !deckGroups || deckGroups.length === 0;

  return (
    <div className={styles.deckGroupList}>
      <div className={styles.deckGroupList__header}>
        <div className={styles.deckGroupList__headerContent}>
          <div>
            <h1 className={styles.deckGroupList__title}>Grouped Decks</h1>
            <p className={styles.deckGroupList__subtitle}>
              Combine multiple decks for comprehensive study sessions
            </p>
          </div>
          <Link to="/quizlets/deck-groups/create">
            <motion.button
              className={styles.deckGroupList__newButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              + New Grouping
            </motion.button>
          </Link>
        </div>
      </div>

      {!isEmpty && (
        <div className={styles.deckGroupList__controls}>
          <input
            type="search"
            className={styles.deckGroupList__search}
            placeholder="Search Grouped Decks..."
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      )}

      {isEmpty ? (
        <motion.div
          className={styles.deckGroupList__empty}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.deckGroupList__emptyIcon}>
            <BookCheckIcon width={64} height={64} />
          </div>
          <h2 className={styles.deckGroupList__emptyTitle}>No grouped decks yet</h2>
          <p className={styles.deckGroupList__emptyText}>
            Create a grouping to combine multiple decks and study them together!
          </p>
          <Link to="/quizlets/deck-groups/create">
            <motion.button
              className={styles.deckGroupList__emptyButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Create Grouping
            </motion.button>
          </Link>
        </motion.div>
      ) : (
        <div className={styles.deckGroupList__grid}>
          {deckGroups.map((group, index) => (
            <DeckGroupCard key={group.id} group={group} index={index} />
          ))}
        </div>
      )}
    </div>
  );
}
